import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, CheckCircle2, ChevronRight, ExternalLink, Landmark, Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Personalized one-click trade dialog.
 *  Routes to whichever broker the user connected: Robinhood (native MCP)
 *  or any SnapTrade trading-enabled brokerage account.
 *  Flow per route: quantity → Review → Confirm & place. */
export default function TradeDialog({ sig, open, onOpenChange }) {
  const { authHeaders } = useAuth();
  const [status, setStatus] = useState(null);
  const [route, setRoute] = useState(null); // {kind:'robinhood'} | {kind:'snaptrade', account}
  const [quantity, setQuantity] = useState("1");
  const [review, setReview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [placed, setPlaced] = useState(null);

  const side = sig?.signal?.includes("SELL") ? "sell" : "buy";

  const fetchStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/brokers/status`, { headers: authHeaders() });
      setStatus(r.data);
      const routes = [];
      if (r.data.robinhood?.connected) routes.push({ kind: "robinhood", label: "Robinhood" });
      (r.data.snaptrade?.accounts || []).filter((a) => a.trading_enabled).forEach((a) =>
        routes.push({ kind: "snaptrade", label: `${a.brokerage || a.institution_name} · ${a.name || a.number}`, account: a })
      );
      if (routes.length === 1) setRoute(routes[0]);
    } catch {
      setStatus({ robinhood: { connected: false }, snaptrade: { accounts: [] } });
    }
  }, [authHeaders]);

  useEffect(() => {
    if (open) {
      setStatus(null); setRoute(null); setReview(null); setPlaced(null); setQuantity("1");
      fetchStatus();
    }
  }, [open, fetchStatus]);

  const connectRobinhood = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/robinhood/connect`, {}, { headers: authHeaders() });
      window.location.href = r.data.authorize_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start Robinhood connection");
      setBusy(false);
    }
  };

  const connectSnapTrade = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/brokers/snaptrade/connect`, {}, { headers: authHeaders() });
      window.location.href = r.data.redirect_uri;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start brokerage connection");
      setBusy(false);
    }
  };

  const doReview = async () => {
    setBusy(true); setReview(null);
    try {
      let r;
      if (route.kind === "robinhood") {
        r = await axios.post(`${API}/robinhood/orders/review`, {
          symbol: sig.symbol, side, quantity: Number(quantity), order_type: "market",
        }, { headers: authHeaders() });
      } else {
        r = await axios.post(`${API}/brokers/snaptrade/orders/review`, {
          symbol: sig.symbol, side, quantity: Number(quantity), account_id: route.account.id,
        }, { headers: authHeaders() });
      }
      setReview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Order review failed");
    } finally {
      setBusy(false);
    }
  };

  const doPlace = async () => {
    setBusy(true);
    try {
      const url = route.kind === "robinhood" ? `${API}/robinhood/orders/place` : `${API}/brokers/snaptrade/orders/place`;
      const r = await axios.post(url, { review_id: review.review_id }, { headers: authHeaders() });
      setPlaced(r.data);
      if (r.data.placed) toast.success(`Order sent via ${route.label}: ${side.toUpperCase()} ${quantity} ${sig.symbol}`);
      else toast.error("Broker rejected the order — see details");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Order placement failed");
    } finally {
      setBusy(false);
    }
  };

  if (!sig) return null;

  const routes = status ? [
    ...(status.robinhood?.connected ? [{ kind: "robinhood", label: "Robinhood" }] : []),
    ...(status.snaptrade?.accounts || []).filter((a) => a.trading_enabled).map((a) =>
      ({ kind: "snaptrade", label: `${a.brokerage || a.institution_name} · ${a.name || a.number}`, account: a })),
  ] : [];

  const reviewLines = review?.review?.content || (review?.impact ? [
    review.notional_estimate ? `Estimated cost: $${Number(review.notional_estimate).toLocaleString()}` : null,
    ...(Array.isArray(review.impact.impacts) ? review.impact.impacts.map((i) => JSON.stringify(i)) : []),
  ].filter(Boolean) : []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-solid max-w-md" data-testid="trade-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#00FFA3]" />
            Trade {sig.symbol}
          </DialogTitle>
          <DialogDescription>
            {side.toUpperCase()} · signal entry ${sig.entry_price?.toLocaleString()}
          </DialogDescription>
        </DialogHeader>

        {status === null && (
          <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-gray-500" /></div>
        )}

        {status && routes.length === 0 && (
          <div className="space-y-3 py-2" data-testid="broker-connect-prompt">
            <p className="text-sm text-gray-400">
              Connect a brokerage to place trades. You sign in securely on your broker's site — we
              never see your password.
            </p>
            <Button onClick={connectRobinhood} disabled={busy} className="w-full bg-[#00FFA3] text-black font-bold hover:bg-[#00E0FF]" data-testid="connect-robinhood-button">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ExternalLink className="w-4 h-4 mr-2" />}
              Connect Robinhood
            </Button>
            <Button onClick={connectSnapTrade} disabled={busy} variant="outline" className="w-full border-white/15" data-testid="connect-snaptrade-button">
              <Landmark className="w-4 h-4 mr-2" />
              Connect another brokerage
            </Button>
            <p className="text-[10px] text-gray-600 text-center">
              Fidelity, Schwab, Webull, IBKR + 30 more via SnapTrade. Trading availability varies by broker.
            </p>
          </div>
        )}

        {status && routes.length > 1 && !route && (
          <div className="space-y-2 py-2" data-testid="broker-route-picker">
            <p className="text-xs text-gray-500 uppercase tracking-wider">Trade with</p>
            {routes.map((r, i) => (
              <button
                key={`${r.kind}-${r.label || i}`}
                onClick={() => setRoute(r)}
                className="w-full flex items-center justify-between rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm text-white hover:bg-white/[0.07]"
                data-testid={`broker-route-${r.kind}-${i}`}
              >
                {r.label}
                <ChevronRight className="w-4 h-4 text-gray-500" />
              </button>
            ))}
          </div>
        )}

        {route && !placed && (
          <div className="space-y-4 py-1">
            <div className="text-[10px] uppercase tracking-wider text-gray-500">
              Via <span className="text-[#00E0FF] font-semibold">{route.label}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <label className="text-xs text-gray-500 uppercase tracking-wider">Shares</label>
                <Input
                  type="number" min="0.01" step="any" value={quantity}
                  onChange={(e) => { setQuantity(e.target.value); setReview(null); }}
                  className="mono mt-1" data-testid="trade-quantity-input"
                />
              </div>
              <div className="text-right">
                <div className="text-xs text-gray-500 uppercase tracking-wider">Est. cost</div>
                <div className="text-lg font-bold mono text-white">
                  ${((Number(quantity) || 0) * (sig.current_price || 0)).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                </div>
              </div>
            </div>

            {review && (
              <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3 text-xs space-y-1" data-testid="trade-review-result">
                <div className="flex items-center gap-1.5 text-[#00E0FF] font-semibold uppercase tracking-wider text-[10px]">
                  <AlertTriangle className="w-3 h-3" /> Broker review
                </div>
                {reviewLines.length === 0 && <p className="text-gray-400">Order validated — ready to place.</p>}
                {reviewLines.map((c, i) => (
                  <p key={`${i}-${String(c).slice(0, 24)}`} className="text-gray-300 whitespace-pre-wrap break-words max-h-40 overflow-y-auto">{c}</p>
                ))}
              </div>
            )}

            {!review ? (
              <Button onClick={doReview} disabled={busy || !(Number(quantity) > 0)} className="w-full bg-[#00E0FF] text-black font-bold hover:bg-[#33E9FF]" data-testid="trade-review-button">
                {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Review order
              </Button>
            ) : (
              <Button onClick={doPlace} disabled={busy} className="w-full bg-[#00FFA3] text-black font-bold hover:bg-[#00FFA3]/80" data-testid="trade-place-button">
                {busy && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Confirm & place {side.toUpperCase()} {quantity} {sig.symbol}
              </Button>
            )}
            <p className="text-[10px] text-gray-600 text-center">
              Real-money order in your brokerage account. Max ${Number(status?.max_order_usd || 5000).toLocaleString()} per order.
            </p>
          </div>
        )}

        {placed && (
          <div className="space-y-3 py-2 text-center" data-testid="trade-order-result">
            <CheckCircle2 className={`w-10 h-10 mx-auto ${placed.placed ? "text-[#00FFA3]" : "text-[#FF3B30]"}`} />
            <p className="text-sm text-gray-300">
              {placed.placed ? `Order sent via ${route.label}` : "The broker did not accept the order"}
            </p>
            <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full" data-testid="trade-close-button">
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
